import type {
  User,
  CreateUserInput,
  UpdateUserInput,
  UserFilters,
  PaginatedUsers,
} from '@lnr-v2/types';
import { apiClient } from './api.client';
import { HTTPError } from 'ky';

/**
 * Database user response type (matches backend response)
 */
interface DbUser {
  id: string;
  email: string;
  username: string | null;
  firstName: string | null;
  lastName: string | null;
  avatar: string | null;
  role: 'SUPER_ADMIN' | 'ADMIN' | 'EDITOR' | 'VIEWER';
  status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED' | 'PENDING_VERIFICATION';
  emailVerified: boolean;
  twoFactorEnabled?: boolean;
  lastLoginAt: string | null;
  lastLoginIp?: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Map database user role to frontend user role
 */
function mapDbRoleToFrontend(
  dbRole: DbUser['role']
): 'admin' | 'editor' | 'viewer' {
  switch (dbRole) {
    case 'SUPER_ADMIN':
    case 'ADMIN':
      return 'admin';
    case 'EDITOR':
      return 'editor';
    case 'VIEWER':
      return 'viewer';
    default:
      return 'viewer';
  }
}

/**
 * Map frontend user role to database user role
 */
function mapFrontendRoleToDb(
  frontendRole: 'admin' | 'editor' | 'viewer'
): DbUser['role'] {
  switch (frontendRole) {
    case 'admin':
      return 'ADMIN';
    case 'editor':
      return 'EDITOR';
    case 'viewer':
      return 'VIEWER';
    default:
      return 'VIEWER';
  }
}

/**
 * Map database user status to frontend user status
 */
function mapDbStatusToFrontend(
  dbStatus: DbUser['status']
): 'active' | 'inactive' | 'pending' {
  switch (dbStatus) {
    case 'ACTIVE':
      return 'active';
    case 'INACTIVE':
    case 'SUSPENDED':
      return 'inactive';
    case 'PENDING_VERIFICATION':
      return 'pending';
    default:
      return 'pending';
  }
}

/**
 * Map frontend user status to database user status
 */
function mapFrontendStatusToDb(
  frontendStatus: 'active' | 'inactive' | 'pending'
): DbUser['status'] {
  switch (frontendStatus) {
    case 'active':
      return 'ACTIVE';
    case 'inactive':
      return 'INACTIVE';
    case 'pending':
      return 'PENDING_VERIFICATION';
    default:
      return 'PENDING_VERIFICATION';
  }
}

/**
 * Transform database user to frontend user format
 */
function transformDbUserToFrontend(dbUser: DbUser): User {
  const name = [dbUser.firstName, dbUser.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();

  return {
    id: dbUser.id,
    name: name || dbUser.email.split('@')[0] || 'Unknown',
    email: dbUser.email,
    role: mapDbRoleToFrontend(dbUser.role),
    status: mapDbStatusToFrontend(dbUser.status),
    avatar: dbUser.avatar || undefined,
    createdAt: dbUser.createdAt,
    lastLoginAt: dbUser.lastLoginAt || undefined,
    updatedAt: dbUser.updatedAt,
  };
}

export const userService = {
  /**
   * Get paginated users with optional filters
   */
  async getUsers(
    page: number = 1,
    pageSize: number = 10,
    filters?: UserFilters
  ): Promise<PaginatedUsers> {
    try {
      // Build query parameters
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      // Add filters
      if (filters?.search) {
        params.append('search', filters.search);
      }
      if (filters?.role && filters.role !== 'all') {
        params.append('role', filters.role);
      }
      if (filters?.status && filters.status !== 'all') {
        params.append('status', filters.status);
      }

      const response = await apiClient.get(`users?${params.toString()}`);
      const data = await response.json<{
        users: DbUser[];
        total: number;
        page: number;
        pageSize: number;
        totalPages: number;
      }>();

      // Transform users to frontend format
      const users = data.users.map(transformDbUserToFrontend);

      return {
        users,
        total: data.total,
        page: data.page,
        pageSize: data.pageSize,
        totalPages: data.totalPages,
      };
    } catch (error) {
      console.error('Error fetching users:', error);
      if (error instanceof HTTPError) {
        const errorData = await error.response.json<{ error: string }>();
        throw new Error(errorData.error || 'Failed to fetch users');
      }
      throw error;
    }
  },

  /**
   * Get a single user by ID
   */
  async getUser(id: string): Promise<User | null> {
    try {
      const response = await apiClient.get(`users/${id}`);
      const data = await response.json<{ user: DbUser }>();

      return data.user ? transformDbUserToFrontend(data.user) : null;
    } catch (error) {
      if (error instanceof HTTPError) {
        if (error.response.status === 404) {
          return null;
        }
        const errorData = await error.response.json<{ error: string }>();
        throw new Error(errorData.error || 'Failed to fetch user');
      }
      console.error('Error fetching user:', error);
      throw error;
    }
  },

  /**
   * Create a new user
   */
  async createUser(input: CreateUserInput): Promise<User> {
    try {
      // Prepare user data with password
      const nameParts = input.name.trim().split(/\s+/);
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      const userData = {
        email: input.email,
        password: input.password, // Use the user-provided password
        firstName,
        lastName,
        username: input.username, // Include username if provided
        role: mapFrontendRoleToDb(input.role),
      };

      const response = await apiClient.post('users', {
        json: userData,
      });

      const data = await response.json<{ user: DbUser }>();
      return transformDbUserToFrontend(data.user);
    } catch (error) {
      if (error instanceof HTTPError) {
        const errorData = await error.response.json<{ error: string }>();
        throw new Error(errorData.error || 'Failed to create user');
      }
      console.error('Error creating user:', error);
      throw error;
    }
  },

  /**
   * Update an existing user
   */
  async updateUser(id: string, input: UpdateUserInput): Promise<User> {
    try {
      // Prepare update data
      const updateData: Record<string, unknown> = {};

      if (input.name !== undefined) {
        const nameParts = input.name.trim().split(/\s+/);
        updateData.firstName = nameParts[0] || '';
        updateData.lastName = nameParts.slice(1).join(' ') || '';
      }

      if (input.email !== undefined) {
        updateData.email = input.email;
      }

      if (input.role !== undefined) {
        updateData.role = mapFrontendRoleToDb(input.role);
      }

      if (input.status !== undefined) {
        updateData.status = mapFrontendStatusToDb(input.status);
      }

      if (input.avatar !== undefined) {
        updateData.avatar = input.avatar;
      }

      const response = await apiClient.put(`users/${id}`, {
        json: updateData,
      });

      const data = await response.json<{ user: DbUser }>();
      return transformDbUserToFrontend(data.user);
    } catch (error) {
      if (error instanceof HTTPError) {
        const errorData = await error.response.json<{ error: string }>();
        throw new Error(errorData.error || 'Failed to update user');
      }
      console.error('Error updating user:', error);
      throw error;
    }
  },

  /**
   * Delete a user (soft delete)
   */
  async deleteUser(id: string): Promise<void> {
    try {
      await apiClient.delete(`users/${id}`);
    } catch (error) {
      if (error instanceof HTTPError) {
        const errorData = await error.response.json<{ error: string }>();
        throw new Error(errorData.error || 'Failed to delete user');
      }
      console.error('Error deleting user:', error);
      throw error;
    }
  },

  /**
   * Delete multiple users (soft delete)
   */
  async deleteUsers(ids: string[]): Promise<void> {
    try {
      await apiClient.post('users/bulk-delete', {
        json: { ids },
      });
    } catch (error) {
      if (error instanceof HTTPError) {
        const errorData = await error.response.json<{ error: string }>();
        throw new Error(errorData.error || 'Failed to delete users');
      }
      console.error('Error deleting users:', error);
      throw error;
    }
  },
};
